import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useRequirePermission } from "../hooks/useHasPermission";
import {
  DEFAULT_PAGE_SIZE,
  parsePaginatedResponse,
} from "../constants/pagination";
import ListPaginationBar from "./ListPaginationBar";
import { getCommunicationMethodIcon } from "../utils/communicationMethodIcons";

const emptyForm = (formFields) =>
  formFields.reduce(
    (acc, f) => ({
      ...acc,
      [f.name]:
        f.defaultValue ?? (f.type === "boolean" ? false : ""),
    }),
    {}
  );

const ReferenceListPage = ({
  title,
  api,
  columns,
  formFields,
  reloadThunk,
  requiredPermission,
  onNavigate,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isRTL = i18n.language === "ar";
  const idField = api.idField;
  const hasAccess = useRequirePermission(requiredPermission, onNavigate);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(() => emptyForm(formFields));
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const refreshReferenceData = useCallback(() => {
    if (reloadThunk) {
      dispatch(reloadThunk());
    }
  }, [dispatch, reloadThunk]);

  const loadItems = useCallback(
    async (pageToLoad = page, sizeToLoad = pageSize) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.list({
          page: pageToLoad,
          page_size: sizeToLoad,
        });
        const { items: listItems, totalCount: count } =
          parsePaginatedResponse(response);
        setItems(listItems);
        setTotalCount(count);
      } catch (err) {
        setError(err.message || t("referencePages.loadError"));
      } finally {
        setIsLoading(false);
      }
    },
    [api, page, pageSize, t]
  );

  useEffect(() => {
    loadItems(page, pageSize);
  }, [page, pageSize]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const openAdd = () => {
    setSelectedItem(null);
    setFormData(emptyForm(formFields));
    setFormErrors({});
    setFormDialogOpen(true);
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    const data = emptyForm(formFields);
    formFields.forEach((f) => {
      if (f.type === "boolean") {
        data[f.name] = Boolean(item[f.name]);
      } else {
        data[f.name] = item[f.name] ?? f.defaultValue ?? "";
      }
    });
    setFormData(data);
    setFormErrors({});
    setFormDialogOpen(true);
  };

  const openDelete = (item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleFieldChange = (name, type) => (event) => {
    const rawValue = event.target.value;
    const value =
      type === "boolean" ? rawValue === true || rawValue === "true" : rawValue;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    formFields.forEach((f) => {
      if (f.type === "boolean") {
        return;
      }
      if (f.required && !String(formData[f.name] ?? "").trim()) {
        errors[f.name] = t("referencePages.fieldRequired");
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    const payload = {};
    formFields.forEach((f) => {
      const value = formData[f.name];
      if (f.type === "boolean") {
        payload[f.name] = Boolean(value);
      } else if (f.type === "number") {
        payload[f.name] = value === "" || value === null ? 0 : Number(value);
      } else if (f.type === "url" && !String(value).trim()) {
        payload[f.name] = null;
      } else {
        payload[f.name] = String(value).trim();
      }
    });
    return payload;
  };

  const renderFormField = (field) => {
    if (field.type === "boolean") {
      return (
        <FormControl
          key={field.name}
          fullWidth
          error={Boolean(formErrors[field.name])}
        >
          <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.name}-label`}
            label={field.label}
            value={formData[field.name] ? "true" : "false"}
            onChange={(event) =>
              handleFieldChange(field.name, "boolean")({
                target: { value: event.target.value === "true" },
              })
            }
          >
            <MenuItem value="true">{t("referencePages.yes")}</MenuItem>
            <MenuItem value="false">{t("referencePages.no")}</MenuItem>
          </Select>
          {formErrors[field.name] && (
            <FormHelperText>{formErrors[field.name]}</FormHelperText>
          )}
        </FormControl>
      );
    }

    if (field.type === "icon-select") {
      return (
        <FormControl
          key={field.name}
          fullWidth
          error={Boolean(formErrors[field.name])}
        >
          <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.name}-label`}
            label={field.label}
            value={formData[field.name]}
            onChange={handleFieldChange(field.name)}
            renderValue={(selected) => {
              const IconComponent = getCommunicationMethodIcon(selected);
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconComponent fontSize="small" />
                  <span>{selected}</span>
                </Box>
              );
            }}
          >
            {(field.options || []).map((iconCode) => {
              const IconComponent = getCommunicationMethodIcon(iconCode);
              return (
                <MenuItem key={iconCode} value={iconCode}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconComponent fontSize="small" />
                    <span>{iconCode}</span>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
          {formErrors[field.name] && (
            <FormHelperText>{formErrors[field.name]}</FormHelperText>
          )}
        </FormControl>
      );
    }

    if (field.type === "select") {
      return (
        <FormControl
          key={field.name}
          fullWidth
          error={Boolean(formErrors[field.name])}
        >
          <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.name}-label`}
            label={field.label}
            value={formData[field.name]}
            onChange={handleFieldChange(field.name)}
          >
            {(field.options || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {formErrors[field.name] && (
            <FormHelperText>{formErrors[field.name]}</FormHelperText>
          )}
        </FormControl>
      );
    }

    return (
      <TextField
        key={field.name}
        label={field.label}
        value={formData[field.name]}
        onChange={handleFieldChange(field.name)}
        error={Boolean(formErrors[field.name])}
        helperText={formErrors[field.name]}
        required={field.required}
        fullWidth
        type={field.type === "url" ? "url" : field.type === "number" ? "number" : "text"}
        multiline={field.multiline}
        rows={field.rows}
        disabled={Boolean(selectedItem && field.readOnlyOnEdit)}
        inputProps={
          field.type === "number"
            ? { min: field.min ?? 0, step: field.step ?? 1 }
            : undefined
        }
      />
    );
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setFormErrors({});
    try {
      const payload = buildPayload();
      if (selectedItem) {
        await api.update(selectedItem[idField], payload);
      } else {
        await api.create(payload);
      }
      setFormDialogOpen(false);
      await loadItems(page, pageSize);
      refreshReferenceData();
    } catch (err) {
      setFormErrors({ general: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.delete(selectedItem[idField]);
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      await loadItems(page, pageSize);
      refreshReferenceData();
    } catch (err) {
      setError(err.message);
      setDeleteDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getItemLabel = (item) => {
    if (isRTL) {
      return item.name_ar || item.name_en || String(item[idField]);
    }
    return item.name_en || item.name_ar || String(item[idField]);
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <Box
      dir={isRTL ? "rtl" : "ltr"}
      sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, maxWidth: "100%", width: "100%" }}
    >
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
            {title}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            size="large"
          >
            {t("referencePages.addNew")}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      align="left"
                      sx={{ fontWeight: "bold" }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      {t("referencePages.noRecords")}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item[idField]} hover>
                      {columns.map((col) => (
                        <TableCell key={col.field} align="left">
                          {col.render
                            ? col.render(item, isRTL)
                            : item[col.field] ?? "—"}
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => openEdit(item)}
                          title={t("common.edit")}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => openDelete(item)}
                          title={t("common.delete")}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && (
          <ListPaginationBar
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            isRTL={isRTL}
          />
        )}
      </Paper>

      <Dialog
        open={formDialogOpen}
        onClose={() => !isSubmitting && setFormDialogOpen(false)}
        maxWidth="md"
        fullWidth
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>
          {selectedItem
            ? t("referencePages.editRecord")
            : t("referencePages.addNew")}
        </DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {formFields.map((f) => renderFormField(f))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFormDialogOpen(false)}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("referencePages.confirmDelete")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("referencePages.deleteConfirmMessage", {
              name: selectedItem ? getItemLabel(selectedItem) : "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isSubmitting}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferenceListPage;
